import {
	GQLOperationalSettingsFragment,
	GQLProductFragment,
	GQLProductSideAvailableDecorationType,
	GQLProductSideSideType,
} from '@theprintbar/graphql';
import { Merch, Size } from '..';
import { cloneDeep, invariant } from '../../../utils';
import { Decoration, getMaxDecorationColours } from '../../decoration';
import { getSortedColours, getSortedSizes } from '../../product';

export type UpdateSource = 'design' | 'sameday' | 'colour' | 'quantity' | 'teamNames' | 'decoration';

interface MerchUpdateProps {
	merch: Merch;
	product: GQLProductFragment;
	activeSide: GQLProductSideSideType;
	isLoading?: boolean;
	source: UpdateSource;
	previousMerch: Merch;
	operationalSettings: GQLOperationalSettingsFragment;
}

export function updateMerchState({ merch, product, activeSide, isLoading, source, previousMerch, operationalSettings }: MerchUpdateProps) {
	let newMerch = cloneDeep(previousMerch);
	switch (source) {
		case 'design':
			newMerch.scene = merch.scene;
			newMerch.decorations = merch.decorations;
			newMerch.teamNames = merch.teamNames;
			break;
		case 'decoration':
			newMerch.sameday = merch.sameday;
			newMerch.scene = merch.scene;
			newMerch.decorations = merch.decorations;
			newMerch.teamNames = merch.teamNames;
			break;
		case 'sameday':
			newMerch.sameday = merch.sameday;
			newMerch.colour = merch.colour;
			newMerch.decorations = merch.decorations;
			break;
		case 'colour':
			newMerch.colour = merch.colour;
			break;
		case 'teamNames':
		case 'quantity':
			newMerch.sizing = merch.sizing;
			newMerch.teamNames = merch.teamNames;
			break;
	}

	newMerch.decorations.forEach((decoration, index) => {
		let error: Decoration['error'];
		try {
			if (!!decoration.artwork?.size) {
				const { width, height, colours } = decoration.meta;

				if (decoration.type === GQLProductSideAvailableDecorationType.Emb) {
					const tooBig = height > operationalSettings.embMaxMM || width > operationalSettings.embMaxMM;
					const tooSmall = width < operationalSettings.embMinMM || height < operationalSettings.embMinMM;

					invariant(!tooBig, `Embroidery design must be less than ${operationalSettings.embMaxMM}mm`);
					invariant(!tooSmall, `Embroidery design must be larger than ${operationalSettings.embMinMM}mm`);
					invariant(!!colours, 'Embroidery design must have colours');
				}
			}
		} catch (e) {
			//If instance of Error object, use the message, otherwise stringify the error
			error = {
				title: 'Decoration error',
				message: e instanceof Error ? e.message : JSON.stringify(e),
			};
		}

		newMerch.decorations[index] = {
			...decoration,
			error,
		};
		return;
	});

	if (source === 'colour' || source === 'sameday') {
		//Make sure the sizing is correct for the new colours
		const newSizing: Size[] = [];

		newMerch.sizing.forEach(({ size, quantity }) => {
			const colourCode = merch?.colour || getSortedColours(product.colours)[0].colour.code;
			const colourSizes = getSortedSizes(product, colourCode).filter(s => !merch?.sameday || s.sameday);
			const matchingSize = colourSizes.find(s => s.variant.size.code === size);
			if (!!matchingSize && matchingSize.stocked) {
				const newQuantity = Math.min(matchingSize.variant.supplierStock ?? matchingSize.variant.warehouseStock, quantity);
				newSizing.push({ size, quantity: newQuantity });
			}
		});
		newMerch.sizing = newSizing;
	}

	return newMerch;
}
